import Link from "next/link";

import {
  bhagyankOf,
  digitsOf,
  mulankOf,
  type BirthDate,
  type Reduction,
} from "@/lib/numerology-digits";
import { LIFE_PATHS, type LifePath } from "@/lib/numerology-life-path-data";

// Result lines for the date-of-birth check on /numerology/<1-9>/ and
// /numerology/life-path/. Every number comes from numerology-digits (the
// engine's reduction) and every theme or gift from LIFE_PATHS, the rows the
// life-path table renders: composed, never authored (rules 23/24).

const LIFE_PATH_BY_NUMBER: Record<string, LifePath> = Object.fromEntries(
  LIFE_PATHS.map((row) => [row.n, row])
);

const RESULT_CLASS =
  "mt-4 space-y-2 border-t border-border-default pt-4 text-body-small leading-relaxed text-text-secondary";
const HEADLINE_CLASS = "text-body font-semibold text-text-primary";
const LINK_CLASS = "font-semibold text-intent-brand underline";
const ORDINAL_SUFFIX: Readonly<Record<number, string>> = { 1: "st", 2: "nd", 3: "rd" };

/** Birth days only (1-31): 1st, 2nd, 11th, 23rd. */
function ordinal(day: number): string {
  if (day >= 11 && day <= 13) return `${day}th`;
  return `${day}${ORDINAL_SUFFIX[day % 10] ?? "th"}`;
}

/**
 * The working as the life-path page writes it: "1+4+0+8+1+9+9+2 = 34 → 3+4 = 7".
 * With `stopAt` (a master number) the working ends where the chain reaches it.
 * @internal
 */
export function workingText(reduction: Reduction, stopAt?: number): string {
  const steps = [`${reduction.digits.join("+")} = ${reduction.chain[0]}`];
  for (let index = 1; index < reduction.chain.length; index += 1) {
    const previous = reduction.chain[index - 1];
    if (previous === stopAt) break;
    steps.push(`${digitsOf(previous).join("+")} = ${reduction.chain[index]}`);
  }
  return steps.join(" → ");
}

/** "the 23rd: 2+3 = 5", or "the 5th, already a single digit". */
function dayWorking(day: number, mulank: Reduction): string {
  if (mulank.digits.length === 1) return `the ${ordinal(day)}, already a single digit`;
  return `the ${ordinal(day)}: ${workingText(mulank)}`;
}

function BhagyankLine({ birth }: { birth: BirthDate }) {
  const bhagyank = bhagyankOf(birth);
  return (
    <p>
      Bhagyank (full date): {workingText(bhagyank)} —{" "}
      {LIFE_PATH_BY_NUMBER[String(bhagyank.value)].theme}.{" "}
      <Link href={`/numerology/life-path/#life-path-${bhagyank.value}`} className={LINK_CLASS}>
        What Bhagyank {bhagyank.value} means
      </Link>
    </p>
  );
}

/** /numerology/<n>/: is the visitor's Mulank this page's number? */
export function MulankDobResult({
  birth,
  pageMulank,
  planetByMulank,
}: {
  birth: BirthDate;
  pageMulank: number;
  planetByMulank: Readonly<Record<number, string>>;
}) {
  const mulank = mulankOf(birth.day);
  const planet = planetByMulank[mulank.value];
  return (
    <div className={RESULT_CLASS} data-numerology-result="mulank" data-mulank={mulank.value}>
      <p>Born on {dayWorking(birth.day, mulank)}.</p>
      {mulank.value === pageMulank ? (
        <p className={HEADLINE_CLASS}>
          Mulank {mulank.value} — this is your number.{" "}
          <a href="#personality-traits" className={LINK_CLASS}>
            Read your personality traits
          </a>
        </p>
      ) : (
        <p className={HEADLINE_CLASS}>
          Your Mulank is {mulank.value}
          {planet ? ` (${planet})` : ""}, not {pageMulank}.{" "}
          <Link href={`/numerology/${mulank.value}/`} className={LINK_CLASS}>
            Read the Mulank {mulank.value} profile
          </Link>
        </p>
      )}
      <BhagyankLine birth={birth} />
    </div>
  );
}

/**
 * /numerology/life-path/: the page's own rule keeps 11, 22 and 33, so a master
 * total is the headline and the engine's single digit stands beside it. Master
 * lines leave the planet out; the date result names only the number and theme.
 */
export function LifePathDobResult({ birth }: { birth: BirthDate }) {
  const bhagyank = bhagyankOf(birth);
  const mulank = mulankOf(birth.day);
  const lifePath = bhagyank.master ?? bhagyank.value;
  const row = LIFE_PATH_BY_NUMBER[String(lifePath)];
  return (
    <div className={RESULT_CLASS} data-numerology-result="life-path" data-life-path={lifePath}>
      <p>Full date: {workingText(bhagyank, bhagyank.master ?? undefined)}</p>
      <p className={HEADLINE_CLASS}>
        Life Path {lifePath} — {row.theme}
        {bhagyank.master ? "" : ` (${row.planet})`}.
      </p>
      <p>
        Your gift is {row.gift}.{" "}
        <a href={`#life-path-${lifePath}`} className={LINK_CLASS}>
          Jump to Life Path {lifePath}
        </a>
      </p>
      {bhagyank.master ? (
        <p>
          {bhagyank.master} reduces to Bhagyank {bhagyank.value} (
          {digitsOf(bhagyank.master).join("+")} = {bhagyank.value}) — the number Ishvaram&rsquo;s
          Bhagyank calculator shows.
        </p>
      ) : null}
      <p>
        Your Mulank is {mulank.value} — born on {dayWorking(birth.day, mulank)}.{" "}
        <Link href={`/numerology/${mulank.value}/`} className={LINK_CLASS}>
          Read the Mulank {mulank.value} profile
        </Link>
      </p>
    </div>
  );
}
